import { getOrientation } from "get-orientation/browser";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Button } from "PFComponents/button";
import { LoadingDots } from "PFComponents/loading_dots";
import { Modal } from "PFComponents/modal";
import { ProfileAvatar } from "PFComponents/profile_avatar/profile_avatar";
import { Slider } from "PFComponents/slider";
import api from "PFCore/api";
import { usePusherEvent } from "PFCore/base/pusher";
import { getCroppedImg, getRotatedImage } from "PFCore/helpers/canvas";
import { getProfileName } from "PFCore/helpers/profile";
import { useCurrentProfileSet } from "PFCore/hooks/queries/profile";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { useProfileAvatarDelete } from "PFCore/hooks/queries/profile/use_profile_avatar_delete";
import { useErrorsGrowl } from "PFCore/hooks/use_errors_growl";
import defaultAvatarUrl from "PFImages/premium/avatar_default.png";
import { Profile } from "PFTypes";
import { ChangeEvent, useCallback, useRef, useState } from "react";
import Cropper, { Area, Point } from "react-easy-crop";
import { useTranslation } from "react-i18next";

import css from "./avatar_uploader.module.scss";

const ORIENTATION_TO_ANGLE: Record<number, number> = {
  3: 180,
  6: 90,
  8: -90
};

const readFile = (file: File) =>
  new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result as string), false);
    reader.readAsDataURL(file);
  });

type PhotoEditModalProps = {
  profile: Profile;
  onClose: () => void;
  onSave: () => void;
  onError: () => void;
};

type AvatarVariant = { url: string };

type PictureProcessedPayload = {
  source: string;
  huge: AvatarVariant;
  rounded: AvatarVariant;
  normal: AvatarVariant;
};

const PhotoEditModal = ({ profile, onClose, onSave, onError }: PhotoEditModalProps) => {
  const { showGrowl } = useGrowlStoreActions();
  const growlErrors = useErrorsGrowl();
  const { t } = useTranslation("profiles", { keyPrefix: "common.avatarUploader" });
  const { t: tTranslation } = useTranslation();
  const { t: tCore } = useTranslation("core");
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const [imageSrc, setImageSrc] = useState<string | null>(
    profile.avatar.custom ? (profile.avatar.fullUrl ?? null) : null
  );
  const [fileName, setFileName] = useState("edited.jpg");
  const [loading, setLoading] = useState(false);

  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(100);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files![0];

    let imageDataUrl = await readFile(file);

    // apply rotation if needed
    const orientation = await getOrientation(file);
    const rotation = ORIENTATION_TO_ANGLE[orientation];
    if (rotation) {
      imageDataUrl = await getRotatedImage(imageDataUrl, rotation);
    }
    setFileName(file.name);
    setImageSrc(imageDataUrl);
  };

  const handleError = (resp: Parameters<typeof growlErrors>[0]) => {
    growlErrors(resp);
    onError();
  };

  const uploadImage = async () => {
    try {
      const blob = await getCroppedImg(imageSrc!, croppedAreaPixels!, rotation);
      setLoading(true);
      const formData = new FormData();
      formData.append("avatar", blob!, fileName.replace(".", `_${new Date().getTime()}.`));

      api({
        method: "POST",
        url: `profiles/${profile.id}/avatar`,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data"
        }
      })
        .then((res) => {
          if (res?.errors?.length) {
            handleError(res);
          } else {
            onClose();
          }
        })
        .catch(() => {
          showGrowl({ kind: "error", message: tCore("errors.networkError") });
          setLoading(false);
        });

      onSave();
    } catch {
      showGrowl({
        kind: "error",
        message: t("errors.couldNotProcess")
      });
    }
  };

  return (
    <Modal
      title={getProfileName(profile)}
      subtitle={t("photoEdit")}
      onOK={uploadImage}
      onClose={onClose}
      labelOK={tTranslation("save")}
      disableOKButton={!imageSrc || loading}
      size="lg"
      classes={{ body: css.modalContentLayout }}
    >
      <div className={css.controls}>
        <Button
          className={css.uploadButton}
          kind="secondary"
          text={t("uploadPhoto")}
          onClick={() => uploadInputRef.current?.click()}
        />
        <input
          className={css.uploadInput}
          name="avatar"
          id="file-upload-input"
          ref={uploadInputRef}
          onChange={onFileChange}
          type="file"
        />
        <Slider
          title={t("zoom")}
          value={zoom}
          min={100}
          max={300}
          step={10}
          showSteps={false}
          disabled={!imageSrc}
          onChange={setZoom}
          instantOnChange
        />
        <Slider
          title={t("rotation")}
          value={rotation}
          min={0}
          max={360}
          step={1}
          showSteps={false}
          disabled={!imageSrc}
          onChange={setRotation}
          instantOnChange
        />
      </div>
      <div className={css.cropperWrapper}>
        <Cropper
          image={imageSrc ?? defaultAvatarUrl}
          crop={crop}
          rotation={rotation}
          zoom={zoom / 100}
          aspect={1}
          onCropChange={setCrop}
          onRotationChange={setRotation}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          cropShape="round"
        />
      </div>
    </Modal>
  );
};

type AvatarUploaderProps = {
  profile: Profile;
  isEditable?: boolean;
};

const AvatarUploader = ({ profile, isEditable = true }: AvatarUploaderProps) => {
  const { data: currentProfile } = useCurrentProfile();
  const setCurrentProfile = useCurrentProfileSet();
  const { showGrowl } = useGrowlStoreActions();
  const growlErrors = useErrorsGrowl();
  const { t } = useTranslation("profiles", { keyPrefix: "common.avatarUploader" });
  const { t: tTranslation } = useTranslation();
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const removeButtonRef = useRef<HTMLButtonElement>(null);

  const { mutateAsync: deleteProfileAvatar, isPending: isDeletingAvatar } = useProfileAvatarDelete();

  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);
  const isCustom = profile?.avatar?.custom;

  const handleAvatarProcessed = useCallback(
    ({ source, huge, rounded, normal }: PictureProcessedPayload) => {
      if (source !== "Profile#picture") {
        return;
      }

      setCurrentProfile({
        ...currentProfile,
        avatar: {
          custom: true,
          fullUrl: huge.url,
          roundedUrl: rounded.url,
          thumbnailUrl: normal.url
        }
      });
      showGrowl({ message: t("profilePicture.saved") });
      setLoading(false);
      setTimeout(() => editButtonRef.current?.focus(), 0);
    },
    [currentProfile, showGrowl, setCurrentProfile, t]
  );

  usePusherEvent(
    `private-profile-${currentProfile.id}`,
    "current_user:picture_processed",
    handleAvatarProcessed
  );

  const handleAvatarRemoved = () => {
    setCurrentProfile({
      ...currentProfile,
      avatar: {
        custom: false
      }
    });
    showGrowl({ message: t("profilePicture.removed") });
  };

  const handleRemove = () => deleteProfileAvatar(profile.id).then(handleAvatarRemoved).catch(growlErrors);

  const isLoading = loading || isDeletingAvatar;

  return (
    <div className={css.avatarUploader}>
      <div className={css.avatarWrapper}>
        {isLoading ? (
          <LoadingDots circleSize="xs" circlesEnabled />
        ) : (
          <ProfileAvatar qaId="profile-edit-picture-picture" profile={profile} size={120} />
        )}
      </div>
      {isEditable && (
        <div className={css.buttons}>
          <Button
            ref={editButtonRef}
            style={{ position: "relative", zIndex: 9 }}
            kind="secondary"
            type="button"
            qaId="profile-edit-update-button"
            disabled={isLoading}
            onClick={() => setShowModal(true)}
            aria-label={t("profilePicture.editProfilePhoto")}
          >
            {t("profilePicture.edit")}
          </Button>
          {isCustom && (
            <Button
              ref={removeButtonRef}
              style={{ position: "relative", zIndex: 9 }}
              kind="danger"
              type="button"
              onClick={() => setShowConfirmRemove(true)}
              disabled={isLoading}
              qaId="profile-edit-picture-remove"
              aria-label={t("profilePicture.removeProfilePhoto")}
            >
              {tTranslation("remove")}
            </Button>
          )}
        </div>
      )}
      {showModal && (
        <PhotoEditModal
          profile={profile}
          onClose={() => {
            setShowModal(false);
            setTimeout(() => editButtonRef.current?.focus(), 0);
          }}
          onSave={() => setLoading(true)}
          onError={() => setLoading(false)}
        />
      )}
      {showConfirmRemove && (
        <Modal
          title={t("profilePicture.removeProfilePhoto")}
          onOK={handleRemove}
          onClose={() => {
            setShowConfirmRemove(false);
            setTimeout(() => (removeButtonRef.current ?? editButtonRef.current)?.focus(), 0);
          }}
        >
          {t("profilePicture.confirmRemove")}
        </Modal>
      )}
    </div>
  );
};

export default AvatarUploader;
